import { NavLink, Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";

// SVG icon definitions matching design
const I = {
  home: '<path d="M3 10.5 12 3l9 7.5"/><path d="M5 9.5V21h14V9.5"/><path d="M9.5 21v-6h5v6"/>',
  play: '<rect x="2.5" y="4" width="19" height="14" rx="2"/><path d="M2.5 21h19"/><path d="m7 9 2.5 2.2L7 13.4"/><path d="M12.5 13.4h4"/>',
  list: '<path d="M8 5h13"/><path d="M8 12h13"/><path d="M8 19h13"/><path d="M3.5 5h.01M3.5 12h.01M3.5 19h.01"/>',
  pin: '<path d="M20 10c0 5-8 11-8 11s-8-6-8-11a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="2.6"/>',
  chart: '<path d="M4 20V10"/><path d="M10 20V4"/><path d="M16 20v-7"/><path d="M22 20H2"/>',
  doc: '<path d="M6 2.5h8l4 4V21a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1Z"/><path d="M13.5 2.5V7h4.5"/><path d="M9 13h6M9 17h6"/>',
  card: '<rect x="2.5" y="5" width="19" height="14" rx="2.5"/><path d="M2.5 9.5h19"/><path d="M6 14.5h4"/>',
  user: '<circle cx="12" cy="8" r="3.5"/><path d="M5 20c0-3.6 3.1-6 7-6s7 2.4 7 6"/>',
  warn: '<path d="M10.3 3.8 2.4 17.5A2 2 0 0 0 4.1 20.5h15.8a2 2 0 0 0 1.7-3L13.7 3.8a2 2 0 0 0-3.4 0Z"/><path d="M12 9v4.5"/><path d="M12 17h.01"/>',
  chev: '<path d="m6 9 6 6 6-6"/>',
  car: '<path d="M5 11l1.4-4.2A2 2 0 0 1 8.3 5.4h7.4a2 2 0 0 1 1.9 1.4L19 11"/><path d="M3 11h18v5a1 1 0 0 1-1 1h-1a1 1 0 0 1-1-1v-1H6v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-5Z"/><circle cx="7.5" cy="14.5" r="1"/><circle cx="16.5" cy="14.5" r="1"/>',
};

function svg(p, w) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={w || 1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      dangerouslySetInnerHTML={{ __html: p }}
    />
  );
}

const NAV_MAIN = [
  { key: "dashboard", label: "Dashboard", icon: I.home, path: "/dashboard" },
  { key: "playground", label: "Playground", icon: I.play, path: "/playground" },
  { key: "collections", label: "Collections", icon: I.list, path: "/collections", subs: [] },
  { key: "postcodes", label: "Postcodes/ZIP", icon: I.pin, path: "/postcodes" },
  { key: "usage", label: "Usage", icon: I.chart, path: "/usage" },
  { key: "docs", label: "Documentation", icon: I.doc, path: "/documentation" },
];

const NAV_ACCT = [
  { key: "account", label: "Account & Billing", icon: I.card, path: "/account" },
  { key: "profile", label: "Profile", icon: I.user, path: "/profile" },
  { key: "errors", label: "Error Logs", icon: I.warn, path: "/error-logs" },
];

// Suppliers section — one sub-group per supplier. Repco for now.
const NAV_REPCO = [
  { key: "repco-catalog", label: "Catalog", icon: I.list, path: "/repco/catalog" },
  { key: "repco-vehicles", label: "Vehicles", icon: I.car, path: "/repco/vehicles" },
];

function NavItem({ item, isActive }) {
  return (
    <>
      <NavLink
        to={item.path}
        className={({ isActive: linkActive }) =>
          `mca-nav__item ${linkActive ? "active" : ""}`
        }
        aria-current={isActive ? "page" : undefined}
      >
        {svg(item.icon)}
        {item.label}
        {item.soon && <span className="mca-nav__badge">Soon</span>}
      </NavLink>
      {item.subs && isActive && item.subs.length > 0 && (
        <>
          {item.subs.map((sub, i) => (
            <NavLink key={i} to={sub.path} className="mca-nav__sub">
              {sub.label}
            </NavLink>
          ))}
        </>
      )}
    </>
  );
}

export const Sidebar = () => {
  const location = useLocation();
  const [profileOpen, setProfileOpen] = useState(false);

  // Determine active nav item based on current path
  const getActiveKey = () => {
    if (location.pathname.includes("/dashboard")) return "dashboard";
    if (location.pathname.includes("/playground")) return "playground";
    if (location.pathname.includes("/repco/vehicles")) return "repco-vehicles";
    if (location.pathname.includes("/repco/catalog")) return "repco-catalog";
    if (location.pathname.includes("/collections")) return "collections";
    if (location.pathname.includes("/postcodes")) return "postcodes";
    if (location.pathname.includes("/usage")) return "usage";
    if (location.pathname.includes("/documentation")) return "docs";
    if (location.pathname.includes("/account") || location.pathname.includes("/plan-payment"))
      return "account";
    if (location.pathname.includes("/profile")) return "profile";
    if (location.pathname.includes("/error-logs")) return "errors";
    return null;
  };

  const activeKey = getActiveKey();

  // Get user data from localStorage
  const AUTH = JSON.parse(window.localStorage.getItem("AUTH") || '{}');
  const userInitials = AUTH.name
    ? AUTH.name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
    : "AL";
  const userEmail = AUTH.email || "alex@brightstore.com.au";
  const userName = AUTH.name || "Alex Lawson";

  const handleLogout = () => {
    window.localStorage.clear();
    window.location.href = "/sign-in";
  };

  const toggleProfileMenu = () => {
    setProfileOpen(!profileOpen);
  };

  return (
    <aside className="mca-sidebar" aria-label="Main navigation">
      {/* Brand */}
      <div className="mca-brand">
        <Link to="/dashboard" className="mca-brand__link" aria-label="MultiCartAPI home">
          <img
            className="mca-brand__logo mca-brand__logo--color"
            src="/assets/brand/multicartapi-logo.png"
            alt="MultiCartAPI"
          />
          <img
            className="mca-brand__logo mca-brand__logo--white"
            src="/assets/brand/multicartapi-logo-white.png"
            alt="MultiCartAPI"
          />
        </Link>
      </div>

      {/* Navigation */}
      <nav className="mca-nav">
        <div className="mca-nav__label">Main</div>
        {NAV_MAIN.map((item) => (
          <NavItem key={item.key} item={item} isActive={activeKey === item.key} />
        ))}

        <div className="mca-nav__label" style={{ marginTop: "10px" }}>
          Account
        </div>
        {NAV_ACCT.map((item) => (
          <NavItem key={item.key} item={item} isActive={activeKey === item.key} />
        ))}

        <div className="mca-nav__label" style={{ marginTop: "10px" }}>Suppliers</div>
        <div className="mca-nav__label" style={{ paddingTop: 2, paddingLeft: 22, fontSize: 10, opacity: 0.75 }}>Repco</div>
        {NAV_REPCO.map((item) => (
          <NavItem key={item.key} item={item} isActive={activeKey === item.key} />
        ))}
      </nav>

      {/* Profile footer */}
      <div
        className={`mca-side-profile${profileOpen ? " is-open" : ""}`}
        data-menu
      >
        <button
          className="mca-side-profile__btn"
          data-menu-trigger
          aria-haspopup="menu"
          aria-expanded={profileOpen}
          onClick={toggleProfileMenu}
        >
          <span className="mca-avatar">{userInitials}</span>
          <span className="mca-side-profile__meta">
            <span className="mca-side-profile__name">{userName}</span>
            <span className="mca-side-profile__email">{userEmail}</span>
          </span>
          {svg(I.chev, 2)}
        </button>
        {profileOpen && (
          <div className="mca-menu" role="menu">
            <NavLink to="/profile" className="mca-menu__item" role="menuitem">
              {svg(I.user)}
              My Profile
            </NavLink>
            <NavLink to="/account" className="mca-menu__item" role="menuitem">
              {svg(I.card)}
              Billing
            </NavLink>
            <div className="mca-menu__sep"></div>
            <button
              className="mca-menu__item mca-menu__item--danger"
              role="menuitem"
              onClick={handleLogout}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M15 4h3a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-3" />
                <path d="M10 17 5 12l5-5" />
                <path d="M5 12h11" />
              </svg>
              Logout
            </button>
          </div>
        )}
      </div>
    </aside>
  );
};
